import { SupabaseClient } from '@supabase/supabase-js';
import { withAdminClient } from '@/utils/adminUtils';

/**
 * Get or create a tag by name. Returns the tag object with id.
 */
export async function getOrCreateTag(supabase: SupabaseClient, tagName: string): Promise<{ id: string } | null> {
  if (!tagName || typeof tagName !== 'string') return null;
  
  return withAdminClient(async (adminClient) => {
    let { data: tag, error: tagError } = await adminClient
      .from('tags')
      .select('id')
      .eq('name', tagName)
      .maybeSingle();
    if (tagError) {
      console.error(`Error finding tag "${tagName}":`, tagError);
      return null;
    }
    if (!tag) {
      const { data: newTag, error: newTagError } = await adminClient
        .from('tags')
        .insert({ name: tagName })
        .select('id')
        .single();
      if (newTagError) {
        console.error(`Error creating tag "${tagName}":`, newTagError);
        return null;
      }
      tag = newTag;
    }
    return tag;
  });
}

/**
 * Add a tag to a solution (if not already linked)
 */
export async function addTagToSolution(supabase: SupabaseClient, solutionId: string, tagId: string): Promise<void> {
  if (!solutionId || !tagId) return;
  
  await withAdminClient(async (adminClient) => {
    const { data: existingRelation, error: relationError } = await adminClient
      .from('solution_tags')
      .select('id')
      .eq('solution_id', solutionId)
      .eq('tag_id', tagId)
      .maybeSingle();
    if (relationError) {
      console.error(`Error checking relation for solution ${solutionId} and tag ${tagId}:`, relationError);
      return;
    }
    if (!existingRelation) {
      const { error: insertError } = await adminClient
        .from('solution_tags')
        .insert({ solution_id: solutionId, tag_id: tagId });
      if (insertError) {
        console.error(`Error inserting relation for solution ${solutionId} and tag ${tagId}:`, insertError);
      }
    }
  });
}

/**
 * Remove a tag from a solution
 */
export async function removeTagFromSolution(supabase: SupabaseClient, solutionId: string, tagId: string): Promise<void> {
  if (!solutionId || !tagId) return;
  
  await withAdminClient(async (adminClient) => {
    const { error } = await adminClient
      .from('solution_tags')
      .delete()
      .eq('solution_id', solutionId)
      .eq('tag_id', tagId);
    if (error) {
      console.error(`Error removing relation for solution ${solutionId} and tag ${tagId}:`, error);
    }
  });
}

/**
 * Get current tags for a solution
 */
export async function getCurrentTags(supabase: SupabaseClient, solutionId: string): Promise<string[]> {
  const { data: tagRelations, error } = await supabase
    .from('solution_tags')
    .select(`tags(id, name)`)
    .eq('solution_id', solutionId);
  
  if (error) {
    console.error(`Error fetching current tags for solution ${solutionId}:`, error);
    return [];
  }
  
  const tags = tagRelations
    ?.map(relation => relation.tags)
    .filter(Boolean)
    .map(tag => (tag as any).name) || [];
    
  return tags.sort();
}

/**
 * Completely sync tags for a solution (add new ones, remove deleted ones)
 */
export async function syncTagsToSolution(supabase: SupabaseClient, solutionId: string, newTags: string[]): Promise<void> {
  // Get current tags
  const currentTags = await getCurrentTags(supabase, solutionId);
  
  // Clean and normalize new tags
  const cleanedNewTags = newTags
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0)
    .filter((tag, index, arr) => arr.indexOf(tag) === index); // Remove duplicates
  
  // Find tags to add and remove
  const tagsToAdd = cleanedNewTags.filter(tag => !currentTags.includes(tag));
  const tagsToRemove = currentTags.filter(tag => !cleanedNewTags.includes(tag));
  
  // Add new tags
  for (const tagName of tagsToAdd) {
    const tag = await getOrCreateTag(supabase, tagName);
    if (tag) {
      await addTagToSolution(supabase, solutionId, tag.id);
    }
  }
  
  // Remove deleted tags
  await withAdminClient(async (adminClient) => {
    for (const tagName of tagsToRemove) {
      const { data: tag } = await adminClient
        .from('tags')
        .select('id')
        .eq('name', tagName)
        .maybeSingle();
      if (tag) {
        await removeTagFromSolution(supabase, solutionId, tag.id);
      }
    }
  });
}

/**
 * Get all existing tags from the database
 */
export async function getAllTags(supabase: SupabaseClient): Promise<string[]> {
  const { data: tags, error } = await supabase
    .from('tags')
    .select('name')
    .order('name');
    
  if (error) {
    console.error('Error fetching tags:', error);
    return [];
  }
  
  return tags?.map(tag => tag.name) || [];
} 